import { useEffect, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { useSelector, shallowEqual } from "react-redux";
import { useTranslation } from "../../common/components/LocalizationProvider";
import { usePremiumTokens } from "../../common/theme/usePremiumTokens";

interface UserOption {
  id: number;
  name?: string;
  email?: string;
}

interface UserNameFilterProps {
  onChange: (userId: string) => void;
  users: UserOption[];
  value?: string;
  allowAllOption?: boolean;
  disabled?: boolean;
}

const EMPTY_USERS_MAP = Object.freeze<Record<number, string | undefined>>({});

const UserNameFilter = ({
  onChange,
  users,
  value = "",
  allowAllOption = true,
  disabled = false,
}: UserNameFilterProps) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const usersMap = useSelector((state) => state.usersMap ?? EMPTY_USERS_MAP, shallowEqual);
  const [selectedValue, setSelectedValue] = useState(value);

  // Premium input styles using tokens
  const formControlSx = {
    minWidth: 240,
    "& .MuiOutlinedInput-root": {
      backgroundColor: tokens.surface.elevated,
      borderRadius: tokens.radius.md,
      transition: tokens.transitions.all,
      "& fieldset": { borderColor: tokens.border.default },
      "&:hover fieldset": { borderColor: tokens.accent.primary },
      "&.Mui-focused fieldset": { borderColor: tokens.accent.primary, borderWidth: 2 },
    },
    "& .MuiInputLabel-root": {
      color: tokens.text.secondary,
      "&.Mui-focused": { color: tokens.accent.primary },
    },
  };

  const selectSx = { backgroundColor: tokens.surface.elevated };

  useEffect(() => {
    setSelectedValue(value ?? "");
  }, [value]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const nextValue = event.target.value ?? "";
    setSelectedValue(nextValue);
    onChange(nextValue);
  };

  const getDisplayName = (user: UserOption) => {
    const fromMap = usersMap[user.id];
    if (fromMap) {
      return fromMap;
    }
    if (user.name) {
      return user.name;
    }
    if (user.email) {
      return user.email;
    }
    return `${user.id}`;
  };

  return (
    <FormControl fullWidth size="small" variant="outlined" sx={formControlSx} disabled={disabled}>
      <InputLabel id="user-select-label">{t("shared.user")}</InputLabel>
      <Select
        labelId="user-select-label"
        value={selectedValue}
        onChange={handleChange}
        label={t("shared.user")}
        displayEmpty
        size="small"
        sx={selectSx}
      >
        {allowAllOption && (
          <MenuItem value="">
            <em>Todos os usuários</em>
          </MenuItem>
        )}
        {users.map((user) => (
          <MenuItem key={user.id} value={user.id.toString()}>
            {getDisplayName(user)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default UserNameFilter;
